import React, { useState, useEffect, useCallback, Fragment, useMemo, useRef } from 'react';
import { LinearProgress, Button, ButtonGroup, Dialog, DialogContent, DialogActions, Toolbar, Typography, Chip, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, Tabs, Tab, Link as MuiLink, List, ListItem as ListItemBase, ListItemText, Grid, Box, Alert } from '@mui/material';
import ExtendedField from '../../components/text_field';
import SelectField from '../../components/select_field';
import Config from '../../config';
import EnhancedTable from '../../components/table';
import ConfirmDialog from '../../components/confirm_dialog';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Moment from 'react-moment';
import Helper, { validateEmail, readFileAsync } from '../../helpers';
import DownloadButton from '../../components/download_button';
import { Form, Field } from 'react-final-form';
import isEqual from 'lodash.isequal';
import Papa from 'papaparse';
import FileSaverBase from 'filesaver.js-npm';
import DropZone from '../files/dropzone';
import { withRouter } from 'react-router';
import { CheckBox } from '@mui/icons-material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import momentjs from 'moment';
import { Root, Header, defaultResultsContainerStyles, pageContentGap } from '../../theme';
import { styled } from '@mui/material/styles';
import CatalogManagerResults from '../../components/CatalogManagerResults';
import { ExportIcon } from '../../utils/icons';

import Tooltip from '@mui/material/Tooltip';
import { PriorityChip, StatusChip, CreatedChip, OnboardingStatus } from './onboarding';
import BarChartIcon from '@mui/icons-material/BarChart';
import OnboardingIntelligenceModal from './components/OnboardingIntelligenceModal';
// Shared types, reused rather than duplicated (per the ticket's guidance to
// share interfaces for concepts common to multiple ccn onboarding surfaces):
// - OnboardingRow is the canonical `/api/odata/integrations` row shape,
//   already shared by onboarding.tsx and the onboarding-intelligence module;
//   that module's own comment calls out onboarding_v2 as an intended
//   consumer.
// - OnboardingCustomFieldConfig/TestCaseGroupsConfig are the exact same
//   `/integration/getconfig` and `/odata/.../GetTestCaseGroups` response
//   shapes already factored out of onboarding_details_v2.tsx (this page
//   hits the same two endpoints). No new onboarding_v2.types.ts file is
//   introduced since there's nothing left that's both v2-specific AND
//   shared with another file — the few shapes unique to this page (the
//   bulk-invite result rows, the bulk-remove progress map) are small enough
//   to define locally below.
import { OnboardingRow } from './onboardingIntelligence.types';
import { OnboardingCustomFieldConfig, TestCaseGroupsConfig } from './onboarding_details.types';

// filesaver.js-npm ships a default export typed as `() => void`; cast to any
// to access the real runtime `.saveAs` method (same pattern used elsewhere,
// e.g. onboarding.tsx, catalog_import.tsx, catalog_export.tsx, product.tsx).
const FileSaver: any = FileSaverBase;

// Relax legacy `button` prop typing on ListItem to `any` (behavior unchanged;
// same pattern established in onboarding.tsx/supplier_onboarding.tsx).
const ListItem: any = ListItemBase;

// styled(Typography) strips the OverridableComponent typing so the
// `component` prop isn't visible without this cast (same pattern established
// in attachments_v2.tsx/events_v2.tsx).
const PageHeading = Header as React.ComponentType<any>;

const ProgressContainer = styled('div')({
    padding: '20px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden'
});

const ProgressItem = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '8px 0'
});

// Cast to ComponentType<any>: this Tooltip is used below with `interactive`,
// a prop MUI's current TooltipProps no longer declares (pre-existing,
// harmless no-op — preserved as-is rather than dropped, to keep this a
// type-only change). Same any-cast pattern established for ExtendedSelect/
// JumpToTab elsewhere in this migration.
const StyledTagTooltip = styled(Tooltip)(() => ({
    '& .MuiTooltip-tooltip': {
        background: '#fff',
        color: '#222',
        border: '1px solid #e0e0e0',
        boxShadow: '0px 1px 8px rgba(0,0,0,0.12)',
        borderRadius: 6,
        padding: '8px 12px',
        fontSize: 14,
        maxWidth: 320,
        minWidth: 180
    }
})) as React.ComponentType<any>;

interface RenderTagsProps {
    value?: string;
    addFilter?: (filter: { operator: string; value: string; field: string }) => void;
}

const RenderTags = ({ value, addFilter }: RenderTagsProps) => {
    const tags = (value || '').split('|').filter(tag => !!tag);
    if (!tags.length) {
        return null;
    }

    const visibleTags = tags.slice(0, 2);
    const overflowTags = tags.slice(2);

    const handleChipClick = (tag: string, event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (addFilter) {
            addFilter({ operator: 'contains', value: tag, field: 'Tags' });
        }
    };

    const renderChip = (tag: string, index: number, options: { withTooltip?: boolean } = {}) => {
        // Typed loosely so the literal 'small'/'outlined' values below don't
        // get widened to `string` (which wouldn't match ChipProps) when spread
        // into <Chip {...chipProps}/>.
        const chipProps: any = {
            size: 'small',
            variant: 'outlined',
            onClick: (event: React.MouseEvent) => handleChipClick(tag, event),
            'aria-label': `Filter list by tag ${tag}`,
            style: {
                maxWidth: '120px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
            },
            label: tag
        };

        if (options.withTooltip) {
            return (
                <Tooltip key={`${tag}-${index}`} title={tag} placement='top' enterDelay={200}>
                    <Chip {...chipProps} />
                </Tooltip>
            );
        }

        return <Chip key={`${tag}-${index}`} {...chipProps} />;
    };

    return (
        <Grid container style={{ gap: 4, flexWrap: 'nowrap', maxWidth: '180px', alignItems: 'center', overflow: 'hidden' }}>
            {visibleTags.map((tag, index) => renderChip(tag, index, { withTooltip: true }))}
            {overflowTags.length > 0 && (
                <StyledTagTooltip
                    title={
                        <Grid container style={{ gap: '8px', flexWrap: 'wrap', padding: '2px 0' }}>
                            {overflowTags.map((tag, index) => renderChip(tag, index))}
                        </Grid>
                    }
                    placement='bottom-start'
                    interactive
                >
                    <Chip size='small' variant='outlined' aria-label={`Show ${overflowTags.length} more tags`} aria-haspopup='true' label='...' />
                </StyledTagTooltip>
            )}
        </Grid>
    );
};

const STATUS_FILTER_OPTIONS = [
    { label: 'All', value: null },
    { label: OnboardingStatus.InviteSent, value: OnboardingStatus.InviteSent },
    { label: OnboardingStatus.FormReceived, value: OnboardingStatus.FormReceived },
    { label: OnboardingStatus.Onboarding, value: OnboardingStatus.Onboarding },
    { label: OnboardingStatus.RequestedGoLive, value: OnboardingStatus.RequestedGoLive },
    { label: 'Live', value: 'Live' }
];

const StatusFilterButtons = ({ table }: { table: any }) => {
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
    const filterState = table?.state?.filter;

    useEffect(() => {
        if (!filterState || !Array.isArray(filterState.filters)) {
            setSelectedStatus(null);
            return;
        }
        const statusFilter = filterState.filters.find((filter: any) => filter.field === 'Status' && !filter.filters);
        if (statusFilter) {
            setSelectedStatus(statusFilter.value);
        } else {
            setSelectedStatus(null);
        }
    }, [filterState]);

    const handleStatusClick = (value: string | null) => {
        if (!table?.extensions?.replaceFilters) {
            return;
        }
        if (selectedStatus === value) {
            return;
        }
        const currentFilters = Array.isArray(filterState?.filters) ? [...filterState.filters] : [];
        const nonStatusFilters = currentFilters.filter((filter: any) => !(filter.field === 'Status' && !filter.filters));

        if (value === null) {
            table.extensions.replaceFilters(nonStatusFilters);
            setSelectedStatus(null);
            return;
        }

        table.extensions.replaceFilters([
            ...nonStatusFilters,
            {
                field: 'Status',
                operator: 'eq',
                value
            }
        ]);
        setSelectedStatus(value);
    };

    if (!table) {
        return null;
    }

    return (
        <Grid
            container
            role='group'
            aria-label='Filter onboarding results by status'
            style={{ gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}
        >
            {STATUS_FILTER_OPTIONS.map(option => (
                <Button
                    key={option.label}
                    color={selectedStatus === option.value ? 'primary' : 'inherit'}
                    onClick={() => handleStatusClick(option.value)}
                    aria-pressed={selectedStatus === option.value}
                    aria-label={option.value ? `Filter by ${option.label}` : 'Show all statuses'}
                >
                    {option.label}
                </Button>
            ))}
        </Grid>
    );
};

// Row shape returned by POST /integration/createmany (bulk invite results),
// specific to this page's bulk-invite dialog — not shared elsewhere, so kept
// local rather than added to a shared types file.
interface BulkInviteResult {
    CompanyName?: string;
    Email?: string;
    Error?: string;
}

// Per-row terminal/interim state for the bulk-remove progress list (LB-432),
// also specific to this page.
type RemoveProgressStatus = 'pending' | 'processing' | 'completed' | 'error';

interface OnboardingV2State {
    newIntegration: boolean;
    showConfirm: boolean;
    showError: boolean;
    selectedTab: string;
    showBulkActions: boolean;
    selected: OnboardingRow[];
    bulkResults: OnboardingRow[];
    testCaseGroups: TestCaseGroupsConfig | Record<string, never>;
    refresh: boolean;
    importFile: File | null;
    created: BulkInviteResult[] | null;
    showIgnored: boolean;
    bulkProgress: number;
    confirmMessage: string;
    onConfirm: (() => void) | null;
    removeActive: boolean;
    removeDone: boolean;
    removeItems: OnboardingRow[];
    removeProgress: Record<string, RemoveProgressStatus>;
    error: string;
    customFields: OnboardingCustomFieldConfig[] | null;
    loaded: boolean;
    generatingReport: boolean;
}

const initialState: OnboardingV2State = {
    newIntegration: false,
    showConfirm: false,
    showError: false,
    selectedTab: 'single',
    showBulkActions: false,
    selected: [],
    bulkResults: [],
    testCaseGroups: {},
    refresh: false,
    importFile: null,
    created: null,
    showIgnored: false,
    bulkProgress: 0,
    confirmMessage: '',
    onConfirm: null,
    removeActive: false,
    removeDone: false,
    removeItems: [],
    removeProgress: {},
    error: '',
    customFields: null,
    loaded: false,
    generatingReport: false
};

// TODO: if a third caller appears, lift this to src/utils/utils.js alongside utilGetPartners.
// Fetch all entries from an OData endpoint by iterating with $skip/$top based on @odata.count.
// (A generic equivalent already lives in ./utils/odata_utils, lifted from
// onboarding_details_v2.tsx for the detail page and the intelligence modal;
// this local copy predates that extraction and is left as-is rather than
// switched over, to keep this migration type-only.)
const fetchAllODataPages = async (initialUrl: string, pageSize: number = 100): Promise<OnboardingRow[]> => {
    const allResults: OnboardingRow[] = [];
    if (!initialUrl) {
        return allResults;
    }

    const [basePath, initialQuery = ''] = initialUrl.split('?');
    const baseParams = new URLSearchParams(initialQuery);
    if (!baseParams.has('$count')) {
        baseParams.set('$count', 'true');
    }

    let skip = 0;
    let totalCount: number | null = null;

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const params = new URLSearchParams(baseParams.toString());
        params.set('$top', pageSize.toString());
        params.set('$skip', skip.toString());

        const pageUrl = `${basePath}?${params.toString()}`;
        const { data } = await axios.get(pageUrl);
        const page = Array.isArray(data.value) ? data.value : [];
        allResults.push(...page);

        skip += page.length;
        const countValue = Number(data['@odata.count']);
        if (!Number.isNaN(countValue)) {
            totalCount = countValue;
        }

        if (!page.length || (totalCount !== null && skip >= totalCount)) {
            break;
        }
    }

    return allResults;
};

const BASE_EXPORT_HEADER = ['Supplier Name', 'Primary Contact', 'Tags', 'Type', 'Vendor Number', 'Status', 'Error', 'Priority', 'Test Group', 'Days Onboarding', 'Communication Attempts', 'Retry Count', 'Started', 'Requested Go Live', 'Live'];

const buildExportHeader = (customFields: OnboardingCustomFieldConfig[] | null) => [...BASE_EXPORT_HEADER, ...(customFields || []).map(field => field.Name)];

interface OnboardingV2Props {
    setTitle?: (title: string, includeInRecents?: boolean) => void;
    isRetailer?: boolean;
    isBillable?: boolean;
    history?: any;
    location?: any;
    match?: any;
}

type OnboardingV2StateUpdate =
    | Partial<OnboardingV2State>
    | ((prev: OnboardingV2State) => Partial<OnboardingV2State>);

const OnboardingV2 = (props: OnboardingV2Props) => {
    const { setTitle, history, location } = props;
    const [state, setState] = useState<OnboardingV2State>(initialState);
    const [showAnalyze, setShowAnalyze] = useState(false);
    const analyzeEnabled = true;

    const updateState = useCallback((updater: OnboardingV2StateUpdate) => {
        setState(prev => {
            const updates = typeof updater === 'function' ? updater(prev) : updater;
            return { ...prev, ...updates };
        });
    }, []);

    const handleSetSelected = useCallback((ids: any[], models: OnboardingRow[]) => {
        setState(prev => {
            if (isEqual(models, prev.selected)) {
                return prev;
            }
            return { ...prev, selected: models };
        });
    }, []);

    const isMounted = useRef(true);
    useEffect(() => () => { isMounted.current = false; }, []);

    const {
        newIntegration,
        showConfirm,
        showError,
        selectedTab,
        showBulkActions,
        selected,
        bulkResults,
        testCaseGroups,
        refresh,
        importFile,
        created,
        showIgnored,
        bulkProgress,
        confirmMessage,
        onConfirm,
        removeActive,
        removeDone,
        removeItems,
        removeProgress,
        error,
        customFields,
        loaded,
        generatingReport
    } = state;

    useEffect(() => {
        let isMounted = true;
        const loadConfig = async () => {
            if (setTitle) {
                setTitle('Supplier Onboarding');
            }
            try {
                const { data: config } = await axios.get(Config.oauth + '/integration/getconfig');
                let testGroups: TestCaseGroupsConfig | Record<string, never> = {};
                if (!Config.production) {
                    const { data } = await axios.get(Config.api + '/odata/Company/Functions.GetTestCaseGroups');
                    testGroups = data;
                }
                if (isMounted) {
                    updateState({
                        customFields: config.CustomFields,
                        loaded: true,
                        testCaseGroups: testGroups
                    });
                }
            } catch (err: any) {
                let message = 'Unexpected error.';
                if (err.response && err.response.data && err.response.data.error) {
                    message = err.response.data.error;
                }
                if (isMounted) {
                    updateState({ loaded: true, showError: true, error: message });
                }
            }
        };

        loadConfig();
        return () => {
            isMounted = false;
        };
    }, [setTitle, updateState]);

    const formatExportDate = useCallback((date: any) => {
        if (!date) {
            return '';
        }
        return momentjs(date).utc().format('YYYY-MM-DD');
    }, []);

    const convertRow = useCallback(
        // `row` stays loosely typed: besides the known OnboardingRow fields it
        // also carries dynamic CustomField1..N keys accessed via row[field.Id]
        // below, which OnboardingRow's interface doesn't (and shouldn't) model
        // (same reasoning already applied to convertRow's `r: any` param in
        // onboarding.tsx).
        (row: any, fields: OnboardingCustomFieldConfig[]) => {
            const baseRow = [
                row.CompanyName,
                row.Email,
                (row.Tags || '').split('|').filter(r => !(!r)).join(','),
                row.IntegrationType,
                row.VendorNumber || '',
                row.Status,
                row.ErrorMessage,
                row.Priority,
                row.TestGroup,
                row.Days,
                row.ReminderCount,
                row.RetryCount,
                formatExportDate(row.StartDate),
                formatExportDate(row.RequestedLiveDate),
                formatExportDate(row.GoLiveDate)
            ];
            (fields || []).forEach(field => {
                baseRow.push(String(row[field.Id] ?? ''));
            });
            return baseRow;
        },
        [formatExportDate]
    );

    const exportSelected = useCallback(() => {
        const header = buildExportHeader(customFields);
        const rows = [header].concat(selected.map(row => convertRow(row, customFields || [])));
        const output = Papa.unparse(rows);
        const blob = new Blob([output], { type: 'text/csv' });
        FileSaver.saveAs(blob, 'onboarding.csv');
    }, [convertRow, customFields, selected]);

    const exportAll = useCallback(async () => {
        updateState({ generatingReport: true });
        try {
            const allRows = await fetchAllODataPages(Config.oauth + '/api/odata/integrations?$orderby=CompanyName asc');
            const header = buildExportHeader(customFields);
            const rows = [header].concat(allRows.map(row => convertRow(row, customFields || [])));
            const output = Papa.unparse(rows);
            const blob = new Blob([output], { type: 'text/csv' });
            if (isMounted.current) {
                FileSaver.saveAs(blob, 'onboarding_report_' + momentjs().format('YYYY-MM-DDTHHmm') + '.csv');
                updateState({ generatingReport: false });
            }
        } catch (err: any) {
            let message = 'Unexpected error.';
            if (err.response && err.response.data && err.response.data.error) {
                message = err.response.data.error;
            }
            if (isMounted.current) {
                updateState({ generatingReport: false, showError: true, error: message });
            }
        }
    }, [convertRow, customFields, updateState]);

    const deleteIntegration = useCallback(async (id: string | number, confirm?: boolean) => {
        if (confirm) {
            try {
                await axios.post(`${Config.oauth}/integration/delete/${id}`);
                updateState(prev => ({ showConfirm: false, refresh: !prev.refresh }));
            } catch (e: any) {
                let message = 'Unexpected error.';
                if (e.response && e.response.data && e.response.data.error) {
                    message = e.response.data.error;
                }
                updateState({ showConfirm: false, showError: true, error: message });
            }
        } else {
            updateState({
                showConfirm: true,
                confirmMessage: 'Are you sure you want to remove this integration? If it has already been deployed to stage it will be moved to "Ignored" priority instead.',
                onConfirm: () => deleteIntegration(id, true)
            });
        }
    }, [updateState]);

    const createIntegration = useCallback(async (values: any) => {
        try {
            await axios.post(Config.oauth + '/integration/create', values);
            updateState(prev => ({ newIntegration: false, refresh: !prev.refresh }));
        } catch (err: any) {
            let message = 'Unexpected error.';
            if (err.response && err.response.data && err.response.data.error) {
                message = err.response.data.error;
            }
            updateState({ showError: true, error: message });
        }
    }, [updateState]);

    const downloadTemplate = useCallback(() => {
        const rows = Config.production ? [['Company', 'Email', 'Priority', 'Note', 'VendorNumber']] : [['Company', 'Email', 'Priority', 'RequestedGoLive', 'Note', 'VendorNumber']];
        if (((testCaseGroups || {}) as TestCaseGroupsConfig).Enabled) {
            rows[0].push('TestGroup');
        }
        const output = Papa.unparse(rows);
        const blob = new Blob([output], { type: 'text/csv' });
        FileSaver.saveAs(blob, 'bulk_onboard_template.csv');
    }, [testCaseGroups]);

    const onDrop = useCallback((files: File[]) => {
        if (files && files.length > 0) {
            updateState({ importFile: files[0] });
        }
    }, [updateState]);

    const bulkInvite = useCallback(async () => {
        try {
            const testGroupsEnabled = ((testCaseGroups || {}) as TestCaseGroupsConfig).Enabled;
            const input = await readFileAsync(importFile);
            const records: any[] = Papa.parse(input, { header: true, skipEmptyLines: true }).data;
            let data = records.map(r => ({
                CompanyName: r.Company,
                Email: r.Email,
                Priority: r.Priority,
                RequestedLiveDate: r.RequestedGoLive,
                Note: r.Note,
                VendorNumber: r.VendorNumber,
                TestGroup: testGroupsEnabled ? r.TestGroup : null
            }));
            if (testGroupsEnabled) {
                let testGroups = data.map(r => r.TestGroup).filter(r => !(!r));
                testGroups = testGroups.filter((r, i) => testGroups.findIndex(x => x === r) === i);
                const invalidTestGroups = testGroups.filter(r => ((((testCaseGroups || {}) as TestCaseGroupsConfig).Groups) || []).findIndex(x => x === r) < 0);
                if (invalidTestGroups.length > 0) {
                    updateState({ showError: true, error: 'These test group(s) do not exist: ' + invalidTestGroups.join(', ') });
                    return;
                }
            }
            const { data: res } = await axios.post(Config.oauth + '/integration/createmany', data);
            updateState(prev => ({ refresh: !prev.refresh, created: res }));
        } catch (err: any) {
            let message = 'Unexpected error.';
            if (err.response && err.response.data && err.response.data.error) {
                message = err.response.data.error;
            }
            updateState({ showError: true, error: message });
        }
    }, [importFile, testCaseGroups, updateState]);

    const bulkAction = useCallback(async (status: string, url: string) => {
        const integrations = selected.filter(r => r.Status === status);
        const results: any[] = [];
        updateState({ bulkProgress: integrations.length ? 0.1 : 0, bulkResults: [] });
        for (let i = 0; i < integrations.length; i++) {
            const integration = integrations[i];
            try {
                await axios.post(url + integration.Id);
            } catch (e: any) {
                let message = 'Unexpected error.';
                if (e.response && e.response.data && e.response.data.error) {
                    message = e.response.data.error;
                }
                results.push({ ...integration, Error: message });
            } finally {
                updateState({ bulkProgress: ((i + 1) / integrations.length) * 100 });
            }
        }
        if (results.length > 0) {
            updateState(prev => ({ bulkResults: results, refresh: !prev.refresh }));
        } else {
            updateState(prev => ({ refresh: !prev.refresh, showBulkActions: false }));
        }
    }, [selected, updateState]);

    const bulkRemove = useCallback(async () => {
        const integrations = [...selected]; // ALL selected, no status filter
        updateState({
            showConfirm: false,
            removeActive: true,
            removeDone: false,
            removeItems: integrations,
            removeProgress: integrations.reduce<Record<string, RemoveProgressStatus>>((acc, integration) => ({ ...acc, [integration.Id]: 'pending' }), {})
        });
        for (let i = 0; i < integrations.length; i++) {
            const integration = integrations[i];
            const id = integration.Id;
            if (isMounted.current) {
                updateState(prev => ({ removeProgress: { ...prev.removeProgress, [id]: 'processing' } }));
            }
            try {
                await axios.post(`${Config.oauth}/integration/delete/${id}`);
                if (isMounted.current) {
                    updateState(prev => ({ removeProgress: { ...prev.removeProgress, [id]: 'completed' } }));
                }
            } catch (e) {
                if (isMounted.current) {
                    updateState(prev => ({ removeProgress: { ...prev.removeProgress, [id]: 'error' } }));
                }
            }
        }
        if (isMounted.current) {
            updateState(prev => ({ refresh: !prev.refresh, removeDone: true }));
        }
    }, [selected, updateState]);

    const showBulkRemoveConfirm = useCallback(() => {
        const n = selected.length;
        const confirmMessage = n === 1
            ? 'Are you sure you want to remove this integration? If it has already been deployed to stage it will be moved to "Ignored" priority instead.'
            : `Are you sure you want to remove these ${n} integrations? If they have already been deployed to stage they will be moved to "Ignored" priority instead.`;
        updateState({ showConfirm: true, confirmMessage, onConfirm: bulkRemove });
    }, [selected, bulkRemove, updateState]);

    const bulkGoLive = useCallback((status: string) => bulkAction(status, Config.oauth + '/integration/golive/'), [bulkAction]);
    const bulkAccept = useCallback((status: string) => bulkAction(status, Config.oauth + '/integration/operatoraccept/'), [bulkAction]);
    const bulkResendInvite = useCallback((status: string) => bulkAction(status, Config.oauth + '/integration/ResendInitialEmail/'), [bulkAction]);
    const bulkResendUserInvite = useCallback((status: string) => bulkAction(status, Config.oauth + '/integration/resendinvite/'), [bulkAction]);

    const showBulkActionsHandler = useCallback(() => {
        updateState({ showBulkActions: true, bulkResults: [], bulkProgress: 0, removeActive: false, removeDone: false, removeItems: [], removeProgress: {} });
    }, [updateState]);

    const closeRemove = useCallback(() => {
        updateState({ showBulkActions: false, removeActive: false, removeDone: false, removeItems: [], removeProgress: {} });
    }, [updateState]);

    // Typed as any[]: later `.push()` calls below add columns whose shape
    // (e.g. the dynamic customFields columns, and the final actions column)
    // doesn't unify with the array's initial-literal-inferred type — same gap
    // (and same fix) as onboarding.tsx's `columns` array in batch 1.
    const baseColumns = useMemo(() => {
        const cols: any[] = [
            { id: 'Id', hidden: true },
            { id: 'Error', hidden: true, type: 'boolean' },
            { id: 'ErrorMessage', hidden: true },
            { id: 'CompanyName', filterable: true, sortable: true, label: 'Supplier Name' },
            {
                id: 'Tags',
                allowStringOperators: true,
                stopPropagation: true,
                filterable: true,
                sortable: false,
                label: 'Tags',
                width: '15em',
                cellStyle: {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                },
                template: (row, column, colIndex, rowIndex, addFilter) => (
                    <RenderTags key={row.Id} value={row[column.id]} addFilter={addFilter} />
                )
            },
            { id: 'Email', hidden: true, toggleable: true, filterable: true, sortable: true, label: 'Primary contact', width: '20em' },
            {
                id: 'Status',
                filterable: true,
                sortable: true,
                label: 'Status',
                width: '13em',
                style: { textOverflow: 'clip' },
                stopPropagation: true,
                    template: (row, column, colIndex, rowIndex, addFilter) => {
                        const value = row[column.id];
                        return (
                            <StatusChip value={value} error={row.ErrorMessage} onClick={() => addFilter({ operator: 'eq', value: value, field: 'Status' })} />
                        );
                    }
                },
                {
                    id: 'Priority',
                    filterable: true,
                    sortable: true,
                    label: 'Priority',
                    width: '10em',
                    stopPropagation: true,
                    template: (row, column, colIndex, rowIndex, addFilter) => {
                        const value = row[column.id];
                        return value ? <PriorityChip value={value} onClick={() => addFilter({ operator: 'eq', value: value, field: 'Priority' })} /> : '';
                    }
                },
            { id: 'TestGroup', hidden: true, toggleable: true, label: 'Test Group' },
            { id: 'IntegrationType', hidden: true, toggleable: true, filterable: true, sortable: true, label: 'Type', width: '10em' },
            { id: 'VendorNumber', hidden: true, toggleable: true, filterable: true, sortable: true, label: 'Vendor Number', width: '12em' },
            { id: 'Days', type: 'number', hidden: Config.production, toggleable: true, filterable: true, sortable: true, label: 'Days Onboarding', width: '15em' },
            {
                id: 'StartDate',
                type: 'date',
                toggleable: true,
                filterable: true,
                hidden: !Config.production,
                sortable: true,
                label: 'Started',
                template: (row, column) => {
                    const value = row[column.id];
                    return value && momentjs(value).isValid() ? <Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment> : '';
                },
                width: '15em'
            },
            {
                id: 'RequestedLiveDate',
                type: 'date',
                hidden: Config.production,
                toggleable: true,
                filterable: true,
                sortable: true,
                label: 'Requested Go Live',
                template: (row, column) => {
                    const value = row[column.id];
                    return value && momentjs(value).isValid() ? <Moment format='MM/DD/YYYY' utc>{value}</Moment> : '';
                },
                width: '12em'
            },
            {
                id: 'GoLiveDate',
                type: 'date',
                hidden: !Config.production,
                toggleable: true,
                filterable: true,
                sortable: true,
                label: 'Live',
                template: (row, column) => {
                    const value = row[column.id];
                    return value && momentjs(value).isValid() ? <Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment> : '';
                },
                width: '15em'
            },
            { id: 'ReminderCount', type: 'number', hidden: true, toggleable: true, filterable: true, sortable: true, label: 'Communication Attempts', width: '15em' },
            { id: 'RetryCount', type: 'number', hidden: true, toggleable: true, filterable: true, sortable: true, label: 'Retry Count', width: '10em' }
        ];
        (customFields || []).forEach(field => {
            cols.push({ id: field.Id, label: field.Name, hidden: true, toggleable: true, filterable: true, sortable: true, style: { textOverflow: 'clip' } });
        });
        cols.push({
            id: 'Id',
            width: '16em',
            stopPropagation: 'true',
            template: (row) => (
                <>
                    <Button variant='outlined' component={Link} to={`/onboarding/integration?id=${row.Id}`} style={{ marginRight: '1em' }}>
                        View
                    </Button>
                    <Button variant='outlined' onClick={() => deleteIntegration(row.Id)}>
                        Remove
                    </Button>
                </>
            )
        });
        return cols;
    }, [customFields, deleteIntegration]);

    const initialTerm = useMemo(() => new Helper().getTermFromUrl(), []);

    // `config` is progressively patched by several effects below with
    // differing branch shapes (see the `filter` ternary and the
    // `customFields` null/array swap) that don't unify cleanly under
    // structural inference — typed as `any` the same way batch 1 typed
    // onboarding.tsx's `columns` array, and consistent with
    // EnhancedTable/CatalogManagerResults's own `config: any` prop type.
    const [config, setConfig] = useState<any>(() => ({
        url: Config.oauth + '/api/odata/integrations',
        savedSearchEnabled: true,
        columns: baseColumns,
        keyField: 'Id',
        order: 'asc',
        orderBy: 'CompanyName',
        pageSize: 20,
        pageSizes: [10, 20, 50, 100],
        filter: showIgnored ? { logic: 'and', filters: [], system: true } : { logic: 'and', filters: [{ operator: 'ne', field: 'Priority', value: 'Ignored', system: true }] },
        filterInUrl: true,
        title: 'Onboardings',
        refresh,
        term: initialTerm,
        selectable: true,
        setSelected: handleSetSelected,
        customFields: customFields || undefined
    }));

    useEffect(() => {
        setConfig(prev => ({ ...prev, columns: baseColumns }));
    }, [baseColumns]);

    useEffect(() => {
        setConfig(prev => ({
            ...prev,
            filter: showIgnored ? { logic: 'and', filters: [], system: true } : { logic: 'and', filters: [{ operator: 'ne', field: 'Priority', value: 'Ignored', system: true }] }
        }));
    }, [showIgnored]);

    useEffect(() => {
        setConfig(prev => ({ ...prev, refresh }));
    }, [refresh]);

    useEffect(() => {
        if (customFields) {
            setConfig(prev => ({ ...prev, customFields }));
        }
    }, [customFields]);

    const bulkResultConfig = useMemo(() => ({
        getData: () => bulkResults,
        columns: [
            { id: 'Id', hidden: true },
            { id: 'CompanyName', filterable: true, sortable: true, label: 'Supplier Name', width: '25em' },
            { id: 'Error', filterable: true, label: 'Error' }
        ],
        keyField: 'Id',
        order: 'asc',
        orderBy: 'CompanyName',
        pageSize: 10,
        pageSizes: [10, 25, 50, 100],
        title: 'Errors'
    }), [bulkResults]);

    const selectedCount = selected.length;
    const hasSelection = selectedCount > 0;
    const bulkActionButtons = (
        <ButtonGroup
            variant='outlined'
            style={{
                visibility: hasSelection ? 'visible' : 'hidden',
                opacity: hasSelection ? 1 : 0
            }}
        >
            <Button disabled={!hasSelection} onClick={showBulkActionsHandler} aria-label='Open bulk actions menu'>
                Bulk Actions
            </Button>
            <Button disabled={!hasSelection} onClick={exportSelected} aria-label={`Export ${selectedCount} selected onboardings`}>
                Export
            </Button>
        </ButtonGroup>
    );

    if (!loaded) {
        return <div />;
    }

    return (
        <Fragment>
            <main role='main' aria-labelledby='page-title'>
                <Root style={{ gap: pageContentGap, padding: '24px 14px 24px 14px' }}>
                    <Grid container justifyContent='space-between' direction='row' alignItems='center' style={{width: '100%'}}>
                            <Grid item>
                                <PageHeading id='page-title' component='h1'>
                                    Supplier Onboarding
                                </PageHeading>
                            </Grid>
                            <Grid item style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }} role='toolbar' aria-label='Page actions'>
                                {analyzeEnabled && (
                                    <Button
                                        variant='outlined'
                                        startIcon={<BarChartIcon fontSize='small' />}
                                        onClick={() => setShowAnalyze(true)}
                                        data-testid='onboarding-intelligence-analyze-button'
                                        aria-label='Analyze onboarding pipeline'
                                    >
                                        Analyze
                                    </Button>
                                )}
                                <Button variant='outlined' component={Link} to={'/onboarding/settings'} aria-label='Open onboarding settings'>
                                    Settings
                                </Button>
                                <Button
                                    variant='outlined'
                                    onClick={() => {
                                        updateState({ showIgnored: !showIgnored });
                                    }}
                                    aria-label='Toggle ignored onboarding records'
                                >
                                    {showIgnored ? 'Hide Ignored' : 'Show Ignored'}
                                </Button>
                                <Button
                                    variant='outlined'
                                    startIcon={<ExportIcon style={{ height: '18px', width: '18px' }} />}
                                    disabled={generatingReport}
                                    onClick={exportAll}
                                >
                                    Generate Report
                                </Button>
                                <Button
                                    variant='contained'
                                    color='primary'
                                    onClick={() => updateState({ newIntegration: true })}
                                    aria-label='Invite suppliers to onboarding'
                                >
                                    Invite Supplier
                                </Button>
                            </Grid>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                        <EnhancedTable
                            config={config}
                            key='onboarding_management_table'
                            template={(table) => (
                                <CatalogManagerResults
                                    showSavedSearch={true}
                                    savedSearchTableType='onboarding'
                                    customFields={config.customFields || {}}
                                    bulkActions={bulkActionButtons}
                                    buttonFilters={<StatusFilterButtons table={table} />}
                                    isSelectable={true}
                                    disableTermInUrl={false}
                                    condensed={false}
                                    table={table}
                                    config={config}
                                    setConfig={setConfig}
                                    resultsContainerStyles={defaultResultsContainerStyles}
                                    renderButtonFiltersAfter={false}
                                    history={history}
                                    location={location}
                                />
                            )}
                        />
                    </Grid>
                </Root>
            </main>
            <Dialog open={showError} onClose={() => updateState({ showError: false })}>
                <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
                <DialogContent>
                    <Typography>{error}</Typography>
                </DialogContent>
            </Dialog>
            <Dialog open={newIntegration} onClose={() => updateState({ newIntegration: false })} fullWidth maxWidth='sm'>
                <Toolbar className='lbtoolbar'>Invite Supplier</Toolbar>
                <Tabs value={selectedTab} onChange={(e, newValue) => updateState({ selectedTab: newValue })} style={{ paddingLeft: '24px', paddingRight: '24px' }}>
                    <Tab label='Single Invite' value='single'></Tab>
                    <Tab label='Bulk Invite' value='bulk'></Tab>
                </Tabs>
                {selectedTab === 'single' && (
                    <Form
                        onSubmit={createIntegration}
                        render={({ handleSubmit, pristine, invalid }) => (
                            <form autoComplete='off' onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <DialogContent>
                                    <div style={{ display: 'flex', flex: '1 1 auto', position: 'relative' }}>
                                        <Field style={{ width: '100%' }} validate={v => (v ? undefined : 'Company name is required.')} label='Company name *' component={ExtendedField} name='CompanyName' />
                                    </div>
                                    <div style={{ display: 'flex', flex: '1 1 auto', position: 'relative' }}>
                                        <Field
                                            style={{ width: '100%' }}
                                            validate={v => (v ? validateEmail(v) : 'Email address is required.')}
                                            label='Primary contact email *'
                                            type='email'
                                            component={ExtendedField}
                                            name='Email'
                                        />
                                    </div>
                                    {((testCaseGroups || {}) as TestCaseGroupsConfig).Enabled && (
                                        <div style={{ display: 'flex', flex: '1 1 auto', position: 'relative' }}>
                                            <Field component={SelectField as any} style={{ paddingBottom: '0.5em' }} formControlProps={{ style: { width: '100%' } }} shrink label='Test group' name='TestGroup'>
                                                <MenuItem value=''>None</MenuItem>
                                                {(testCaseGroups as TestCaseGroupsConfig).Groups!.map(r => (
                                                    <MenuItem key={r} value={r}>
                                                        {r}
                                                    </MenuItem>
                                                ))}
                                            </Field>
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', flex: '1 1 auto', position: 'relative' }}>
                                        <Field component={SelectField as any} style={{ paddingBottom: '0.5em' }} formControlProps={{ style: { width: '100%' } }} shrink name='Priority' label='Priority'>
                                            <MenuItem value=''>None</MenuItem>
                                            <MenuItem value='Ignored'>
                                                <PriorityChip size='small' value='Ignored' />
                                            </MenuItem>
                                            <MenuItem value='Low'>
                                                <PriorityChip size='small' value='Low' />
                                            </MenuItem>
                                            <MenuItem value='Medium'>
                                                <PriorityChip size='small' value='Medium' />
                                            </MenuItem>
                                            <MenuItem value='High'>
                                                <PriorityChip size='small' value='High' />
                                            </MenuItem>
                                        </Field>
                                    </div>
                                    {!Config.production && (
                                        <div style={{ display: 'flex', flex: '1 1 auto', position: 'relative' }}>
                                            <Field style={{ width: '100%' }} label='Requested live date' type='date' component={ExtendedField} name='RequestedLiveDate' />
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', flex: '1 1 auto', position: 'relative' }}>
                                        <Field style={{ width: '100%' }} label='Note' component={ExtendedField} name='Note' />
                                    </div>
                                    <div style={{ display: 'flex', flex: '1 1 auto', position: 'relative' }}>
                                        <Field style={{ width: '100%' }} label='Vendor number' component={ExtendedField} name='VendorNumber' />
                                    </div>
                                </DialogContent>
                                <DialogActions>
                                    <DownloadButton variant='text' color='primary' onClick={handleSubmit} type='submit' disabled={pristine || invalid}>
                                        Send Invite
                                    </DownloadButton>
                                </DialogActions>
                            </form>
                        )}
                    />
                )}
                {selectedTab === 'bulk' && (
                    <>
                        {!created && (
                            <DialogContent style={{ overflow: 'hidden' }}>
                                <Typography>
                                    <MuiLink style={{ lineHeight: 'inherit', fontSize: 'inherit', fontFamily: 'inherit', verticalAlign: 'top' }} underline="hover" component='button' onClick={downloadTemplate}>
                                        Download template
                                    </MuiLink>{' '}
                                    to bulk upload up to 50 suppliers.
                                </Typography>
                                <Table size='small'>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ width: '6em' }}>Field</TableCell>
                                            <TableCell style={{ width: '5em' }}>Required</TableCell>
                                            <TableCell>Description</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Company</TableCell>
                                            <TableCell>
                                                <CheckBox aria-hidden='true' role='presentation' focusable='false' />
                                            </TableCell>
                                            <TableCell>Company name</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Email</TableCell>
                                            <TableCell>
                                                <CheckBox aria-hidden='true' role='presentation' focusable='false' />
                                            </TableCell>
                                            <TableCell>Primary contact email</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Priority</TableCell>
                                            <TableCell></TableCell>
                                            <TableCell>Low, Medium, or High</TableCell>
                                        </TableRow>
                                        {!Config.production && (
                                            <TableRow>
                                                <TableCell>RequestedGoLive</TableCell>
                                                <TableCell></TableCell>
                                                <TableCell>Format MM/DD/YYYY</TableCell>
                                            </TableRow>
                                        )}
                                        <TableRow>
                                            <TableCell>Note</TableCell>
                                            <TableCell></TableCell>
                                            <TableCell>Additional message in the initial email</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>VendorNumber</TableCell>
                                            <TableCell></TableCell>
                                            <TableCell>Supplier vendor number</TableCell>
                                        </TableRow>
                                        {!!(((testCaseGroups || {}) as TestCaseGroupsConfig).Enabled) && (
                                            <TableRow>
                                                <TableCell>TestGroup</TableCell>
                                                <TableCell></TableCell>
                                                <TableCell>Supplier test group</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                                <DropZone accept='.csv' style={{ width: '100%', height: '10em' }} onDrop={onDrop}>
                                    {({ isDragAccept, isDragReject }) => {
                                        if (isDragAccept || isDragReject) {
                                            return 'Drop here to upload this file.';
                                        }
                                        return importFile ? importFile.name : 'Drop a CSV file here or click to choose a file.';
                                    }}
                                </DropZone>
                            </DialogContent>
                        )}
                        {created && (
                            <DialogContent style={{ padding: 0 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ width: '12em' }}>Company</TableCell>
                                            <TableCell style={{ width: '12em' }}>Primary contact</TableCell>
                                            <TableCell style={{ width: '10em' }}>Result</TableCell>
                                            <TableCell>Message</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {created.map((r, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{r.CompanyName}</TableCell>
                                                <TableCell>{r.Email}</TableCell>
                                                <TableCell>
                                                    <CreatedChip value={!r.Error} />
                                                </TableCell>
                                                <TableCell>{r.Error}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </DialogContent>
                        )}
                        {!created && (
                            <DialogActions>
                                <DownloadButton variant='text' color='primary' onClick={bulkInvite} type='submit' disabled={!importFile || !importFile.name}>
                                    Send Invites
                                </DownloadButton>
                            </DialogActions>
                        )}
                    </>
                )}
            </Dialog>
            <Dialog maxWidth={removeActive ? 'sm' : (bulkResults.length > 0 ? 'md' : 'xs')} fullWidth open={showBulkActions} onClose={() => { if (!removeActive || removeDone) { updateState({ showBulkActions: false }); } }}>
                {removeActive && (
                    <>
                        <Toolbar className='lbtoolbar'>{removeDone ? 'Removal Complete' : 'Removing Suppliers...'}</Toolbar>
                        <ProgressContainer style={{ minHeight: 0 }}>
                            {(() => {
                                const completedCount = removeItems.filter(item => removeProgress[item.Id] === 'completed').length;
                                const errorCount = removeItems.filter(item => removeProgress[item.Id] === 'error').length;
                                const finishedCount = completedCount + errorCount;
                                const total = removeItems.length || 1;
                                return (
                                    <>
                                        {!removeDone && (
                                            <LinearProgress variant='determinate' value={(finishedCount / total) * 100} />
                                        )}
                                        {removeDone && (
                                            <Alert severity={errorCount === 0 ? 'success' : (completedCount === 0 ? 'error' : 'warning')} style={{ marginTop: '8px' }}>
                                                    {errorCount === 0
                                                        ? `Successfully removed ${completedCount} supplier${completedCount !== 1 ? 's' : ''}.`
                                                        : (completedCount === 0
                                                            ? `Removal failed for all ${errorCount} supplier${errorCount !== 1 ? 's' : ''}.`
                                                            : `Removed ${completedCount} supplier${completedCount !== 1 ? 's' : ''}, ${errorCount} failed.`)}
                                            </Alert>
                                        )}
                                        <Box style={{ maxHeight: '240px', overflow: 'auto', marginTop: '8px' }}>
                                            {removeItems.map(item => {
                                                const status = removeProgress[item.Id];
                                                return (
                                                    <ProgressItem key={item.Id}>
                                                        <Typography variant='body2'>{item.CompanyName}</Typography>
                                                        <div>
                                                            {status === 'completed' && <Typography color='primary'>✓ Completed</Typography>}
                                                            {status === 'error' && <Typography color='error'>✗ Error</Typography>}
                                                            {status === 'processing' && <Typography>Processing...</Typography>}
                                                            {(status === 'pending' || !status) && <Typography color='textSecondary'>Pending</Typography>}
                                                        </div>
                                                    </ProgressItem>
                                                );
                                            })}
                                        </Box>
                                    </>
                                );
                            })()}
                        </ProgressContainer>
                        {removeDone && (
                            <DialogActions>
                                <Button onClick={closeRemove}>Close</Button>
                            </DialogActions>
                        )}
                    </>
                )}
                {!removeActive && bulkResults.length === 0 && bulkProgress === 0 && (
                    <>
                        <Toolbar className='lbtoolbar'>Bulk Onboarding Actions</Toolbar>
                        <List style={{ padding: 0 }}>
                            <ListItem button disabled={!selected.filter(r => r.Status === OnboardingStatus.InviteSent).length} onClick={() => bulkResendInvite(OnboardingStatus.InviteSent)}>
                                <ListItemText primary='Resend onboarding invite' secondary={`${selected.filter(r => r.Status === OnboardingStatus.InviteSent).length} selected companies in ${OnboardingStatus.InviteSent} status`} />
                                <ArrowForwardIosIcon color='action' />
                            </ListItem>
                            <ListItem button disabled={!selected.filter(r => r.Status === OnboardingStatus.FormReceived).length} onClick={() => bulkAccept(OnboardingStatus.FormReceived)}>
                                <ListItemText primary='Accept onboarding form' secondary={`${selected.filter(r => r.Status === OnboardingStatus.FormReceived).length} selected companies in ${OnboardingStatus.FormReceived} status`} />
                                <ArrowForwardIosIcon color='action' />
                            </ListItem>
                            <ListItem button disabled={!selected.filter(r => r.Status === OnboardingStatus.Onboarding).length} onClick={() => bulkResendUserInvite(OnboardingStatus.Onboarding)}>
                                <ListItemText primary='Resend user invite' secondary={`${selected.filter(r => r.Status === OnboardingStatus.Onboarding).length} selected companies in ${OnboardingStatus.Onboarding} status`} />
                                <ArrowForwardIosIcon color='action' />
                            </ListItem>
                            <ListItem button disabled={!selected.filter(r => r.Status === OnboardingStatus.Onboarding).length} onClick={() => bulkGoLive(OnboardingStatus.Onboarding)}>
                                <ListItemText primary='Push supplier live' secondary={`${selected.filter(r => r.Status === OnboardingStatus.Onboarding).length} selected companies in ${OnboardingStatus.Onboarding} status`} />
                                <ArrowForwardIosIcon color='action' />
                            </ListItem>
                            <ListItem button disabled={!selected.filter(r => r.Status === OnboardingStatus.RequestedGoLive).length} onClick={() => bulkGoLive(OnboardingStatus.RequestedGoLive)}>
                                <ListItemText primary='Approve to go live' secondary={`${selected.filter(r => r.Status === OnboardingStatus.RequestedGoLive).length} selected companies in ${OnboardingStatus.RequestedGoLive} status`} />
                                <ArrowForwardIosIcon color='action' />
                            </ListItem>
                            <ListItem button disabled={!selected.length} onClick={showBulkRemoveConfirm}>
                                <ListItemText primary='Remove suppliers' secondary={`${selected.length} supplier${selected.length !== 1 ? 's' : ''} selected`} />
                                <ArrowForwardIosIcon color='action' />
                            </ListItem>
                        </List>
                    </>
                )}
                {!removeActive && bulkResults.length === 0 && bulkProgress > 0 && (
                    <>
                        <Toolbar className='lbtoolbar'>Processing Bulk Action</Toolbar>
                        <DialogContent>
                            <div style={{ display: 'flex', flex: 1, placeItems: 'center', justifyContent: 'center' }}>
                                <LinearProgress style={{ height: '50px', width: '150px' }} variant='determinate' value={bulkProgress} />
                            </div>
                        </DialogContent>
                    </>
                )}
                {!removeActive && (bulkResults.length > 0 ? <EnhancedTable panelStyle={{ boxShadow: 'none' }} wrapperStyle={{ maxHeight: '400px' }} config={bulkResultConfig} /> : <span />)}
            </Dialog>
            <ConfirmDialog open={showConfirm} onClose={() => updateState({ showConfirm: false })} message={confirmMessage} onConfirm={onConfirm} />
            {analyzeEnabled && (
                <OnboardingIntelligenceModal open={showAnalyze} onClose={() => setShowAnalyze(false)} />
            )}
        </Fragment>
    );
};

export default withRouter(OnboardingV2 as any);
export { PriorityChip };
